/*
  Carnevore Mountain email sign-up (sections/cm-raw-signup.liquid). Loaded with defer; no libraries, and no Klaviyo
  script: the form talks to Klaviyo's Client Subscriptions API directly.
  <cm-raw-signup>: on submit, posts the email to
    https://a.klaviyo.com/client/subscriptions?company_id=<public key>
  with email marketing consent, the list and a custom source (data-key, data-list, data-source).
  - Accepted (202): the field gives way to the thank-you message, which takes focus.
  - Refused (400, a bad address): the error shows, the field is marked invalid, and the error takes focus.
  - Klaviyo can't be reached, or no key or list is set: the form is sent to Shopify as it is (the customer form, the
    no-script path), so no sign-up is lost.
  If Klaviyo's own script is on the page (the Shopify app embed), the visitor is identified, so Klaviyo's pop-up
  doesn't ask them to sign up again.
*/
if (!customElements.get('cm-raw-signup')) {
  customElements.define(
    'cm-raw-signup',
    class CmRawSignup extends HTMLElement {
      connectedCallback() {
        this.form = this.querySelector('form');
        if (!this.form) return;
        // Shopify's own redirect after a sent form shows the thank-you message already: that counts as signed up too.
        if (this.form.querySelector('[data-signup-sent]:not([hidden])')) this.silencePopup();
        this.input = this.form.querySelector('input[type="email"]');
        this.button = this.form.querySelector('button[type="submit"]');
        this.sent = this.form.querySelector('[data-signup-sent]');
        this.error = this.form.querySelector('[data-signup-error]');
        this.fields = this.form.querySelector('.cm-raw-signup__fields');
        if (!this.input || !this.sent || !this.error) return;
        this.form.addEventListener('submit', (event) => this.submit(event));
        this.input.addEventListener('input', () => {
          if (this.error.hidden) return;
          this.error.hidden = true;
          this.input.removeAttribute('aria-invalid');
        });
      }

      async submit(event) {
        const { key, list, source } = this.dataset;
        if (!key || !list || this.fallback) return; // Shopify's customer form takes it
        event.preventDefault();
        const email = this.input.value.trim();
        if (!this.input.checkValidity() || !email) {
          this.fail();
          return;
        }
        this.busy(true);
        let response = null;
        try {
          response = await fetch(`https://a.klaviyo.com/client/subscriptions?company_id=${encodeURIComponent(key)}`, {
            method: 'POST',
            headers: { 'content-type': 'application/vnd.api+json', accept: 'application/vnd.api+json', revision: '2024-10-15' },
            body: JSON.stringify({
              data: {
                type: 'subscription',
                attributes: {
                  custom_source: source || undefined,
                  profile: {
                    data: {
                      type: 'profile',
                      attributes: { email, subscriptions: { email: { marketing: { consent: 'SUBSCRIBED' } } } },
                    },
                  },
                },
                relationships: { list: { data: { type: 'list', id: list } } },
              },
            }),
          });
        } catch (e) {
          response = null;
        }
        this.busy(false);
        if (response && response.ok) {
          this.done(email);
        } else if (response && response.status >= 400 && response.status < 500 && response.status !== 429) {
          this.fail();
        } else {
          // Klaviyo is down or busy: send the form to Shopify instead (Klaviyo's Shopify sync picks it up).
          this.fallback = true;
          this.form.requestSubmit ? this.form.requestSubmit() : this.form.submit();
        }
      }

      busy(on) {
        this.button.disabled = on;
        this.button.textContent = on ? this.button.dataset.sending : this.button.dataset.label;
      }

      done(email) {
        // The message takes the field's place at the field's height, so nothing below moves.
        if (this.fields) this.form.style.setProperty('--cm-signup-height', `${this.fields.offsetHeight}px`);
        this.form.classList.add('is-sent');
        this.error.hidden = true;
        this.sent.hidden = false;
        this.sent.focus();
        this.silencePopup();
        try {
          if (window.klaviyo && typeof window.klaviyo.identify === 'function') window.klaviyo.identify({ email });
        } catch (e) {
          /* Klaviyo's script isn't needed here; identifying is a courtesy for its pop-up. */
        }
      }

      // With data-silence-popup (article pages): the welcome pop-up remembers this visitor as signed up, so it never
      // asks them again (its own "done" key; see assets/cm-raw-popup.js).
      silencePopup() {
        if (!this.hasAttribute('data-silence-popup')) return;
        try {
          window.localStorage.setItem('cm-raw-popup-done', String(Date.now()));
        } catch (e) {
          /* the pop-up just comes back next visit */
        }
      }

      fail() {
        this.input.setAttribute('aria-invalid', 'true');
        this.error.hidden = false;
        this.error.focus();
      }
    }
  );
}
